import classNames from "classnames";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import useDocumentTitle from "../../utils/useDocumentTitle";
import styles from "./Settings.module.scss";

import Button from "../../components/Button";
import PageContent from "../../components/PageContent";
import PasswordField from "../../components/PasswordField";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import { DashboardLayout } from "../../layouts/DashboardLayout";

import { useTranslation } from "react-i18next";
import { useNotification } from "../../context/notification";
import { apiRequest } from "../../utils/api";

export function ChangePassword(props) {
  const { t } = useTranslation();
  useDocumentTitle(props.title);
  const history = useHistory();
  const { addNotification } = useNotification();
  const [disablePasswordSubmit, setDisablePasswordSubmit] = useState(false);
  const [isSamePassword, setIsSamePassword] = useState(false);
  const [isStrongPassword, setIsStrongPassword] = useState(false);
  const [isMatchingPassword, setIsMatchingPassword] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    showOldPassword: false,
    showNewPassword: false,
    showNewPasswordConfirmation: false,
    oldPassword: "",
    newPassword: "",
    newPasswordConfirmation: "",
    isDirty: false,
  });

  useEffect(() => {
    // Redirect user after first password change
    const isFirstTime = localStorage.getItem("first_login");
    const targetPath = localStorage.getItem("target_path");

    if (isFirstTime === "0" && targetPath) {
      history.push(targetPath);
      localStorage.removeItem("target_path");
    }
  }, [history]);

  useEffect(() => {
    if (passwordForm.isDirty) {
      validateStrength();
      validateNewPassword();
      validateMatch();
    }
    // eslint-disable-next-line
  }, [passwordForm.newPassword, passwordForm.oldPassword, passwordForm.newPasswordConfirmation]);

  const handlePasswordFormEventChange = prop => event => {
    setPasswordForm({
      ...passwordForm,
      isDirty: true,
      [prop]: event.target.value,
    });
  };

  const handleClickShowPassword = prop => () => {
    setPasswordForm({
      ...passwordForm,
      [prop]: !passwordForm[prop],
    });
  };

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  const handlePasswordSubmit = () => {
    let valid = validateMatch() && validateStrength() && !validateNewPassword();
    if (valid) {
      setDisablePasswordSubmit(true);
      let formState = passwordForm;
      apiRequest
        .post("/api/changePassword", {
          uid: localStorage.getItem("uid"),
          old_password: formState.oldPassword,
          new_password: formState.newPassword,
          confirm_password: formState.newPasswordConfirmation,
          first_login: 0,
        })
        .then(response => {
          if (response.status === 400) {
            addNotification(t(response.data.msg), "error");
            return setDisablePasswordSubmit(false);
          }
          if (response.data && response.data.msg) {
            addNotification(t(response.data.msg), "success");
          } else {
            addNotification("Password settings saved!", "success");
          }

          localStorage.setItem("first_login", 0);
          // reload page on successful password change
          setTimeout(() => {
            const hasCompleted =
              localStorage.getItem("has_completed_security_questions") === "1";
            if (!hasCompleted) {
              history.replace("/security-question/setup");
            } else {
              history.go(0);
            }
          }, 800);
        })
        .catch(error => {
          console.error("Failed to save Password settings!", error.response);
          if (error.response.status === 400 && error.response.data.msg) {
            addNotification(t(error.response.data.msg), "error");
          } else {
            addNotification(
              "Failed to save Password settings! Please refresh the page and try again.",
              "error"
            );
          }
          setDisablePasswordSubmit(false);
        });
    } else {
      addNotification(
        "Can't change password! Please refresh the page or check if your new password meets the requirements."
      );
    }
  };
  const validateNewPassword = () => {
    setIsSamePassword(passwordForm.newPassword === passwordForm.oldPassword);
    return passwordForm.newPassword === passwordForm.oldPassword
  }

  const validateMatch = () => {
    setIsMatchingPassword(passwordForm.newPasswordConfirmation === passwordForm.newPassword);
    return passwordForm.newPasswordConfirmation === passwordForm.newPassword
  };

  const validateStrength = () => {
    const lengthCheck = passwordForm.newPassword.length >= 12;
    const numberCheck = /\d/.test(passwordForm.newPassword);
    const specialCheck = /[ !"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/.test(passwordForm.newPassword);
    const upperCaseCheck = /[A-Z]/.test(passwordForm.newPassword);

    // Password must be at least 12 characters long, one number, one special character and one upper case letter.
    setIsStrongPassword(lengthCheck && numberCheck && specialCheck && upperCaseCheck);
    return lengthCheck && numberCheck && specialCheck && upperCaseCheck;
  };

  return (
    <DashboardLayout>
      <div className={styles.pageWrapper}>
        <PageContent>
          <Section>
            <SectionTitle>{t("leftNavigation.changePassword")}</SectionTitle>
            <div className={styles.detailsContainer}>
              <div className="d-flex">
                <form
                  className={classNames(
                    styles.passwordSection,
                    "padding-top-10"
                  )}
                >
                  <PasswordField
                    className={classNames(
                      styles.passwordField,
                      "margin-bottom-24"
                    )}
                    autoComplete="current-password"
                    label={t("changePassword.enterYourCurrentPassowrd")}
                    show={passwordForm.showOldPassword}
                    value={passwordForm.password}
                    onChange={handlePasswordFormEventChange("oldPassword")}
                    onShowClick={handleClickShowPassword('showOldPassword')}
                    onShowMouseDown={handleMouseDownPassword}
                  />
                  <span
                    className={
                      "grey-600 margin-bottom-24 margin-top-4 text-body-2"
                    }
                  >
                    {t("changePassword.chooseAStrongPassword")}
                  </span>
                  <PasswordField
                    className={classNames(
                      styles.passwordField,
                      "margin-bottom-24"
                    )}
                    autoComplete="new-password"
                    label={t("changePassword.newPassword")}
                    show={passwordForm.showNewPassword}
                    value={passwordForm.password}
                    onChange={handlePasswordFormEventChange("newPassword")}
                    onShowClick={handleClickShowPassword('showNewPassword')}
                    onShowMouseDown={handleMouseDownPassword}
                  />
                  {passwordForm.newPassword.length > 0 &&
                    passwordForm.isDirty &&
                    !isStrongPassword && (
                      <span className={"margin-bottom-24 red-700"}>
                        {t("changePassword.passwordMustBeAtLeast12")}
                      </span>
                    )}
                  {passwordForm.newPassword.length > 0 &&
                    passwordForm.isDirty &&
                    isSamePassword && (
                      <span className={"margin-bottom-24 red-700"}>
                        {t("changePassword.passwordCannotBeSameAsOldPassword")}
                      </span>
                    )}
                  <div className={"text-medium margin-top-4 text-body-2"}>
                    {t("changePassword.passwordStrength")}
                  </div>
                  <span className={"grey-600 margin-bottom-24 text-body-2"}>
                    {t("changePassword.useAtLeast12Charecters")}
                  </span>
                  <PasswordField
                    className={classNames(
                      styles.passwordField,
                      "margin-bottom-24"
                    )}
                    autoComplete="new-password"
                    label={t("changePassword.confirmNewPassword")}
                    show={passwordForm.showNewPasswordConfirmation}
                    value={passwordForm.password}
                    onChange={handlePasswordFormEventChange(
                      "newPasswordConfirmation"
                    )}
                    onShowClick={handleClickShowPassword('showNewPasswordConfirmation')}
                    onShowMouseDown={handleMouseDownPassword}
                  />
                  {passwordForm.newPasswordConfirmation.length > 0 &&
                    passwordForm.isDirty &&
                    !isMatchingPassword && (
                      <span className={"margin-bottom-24 red-700"}>
                        {t("changePassword.thePasswordDoNotMatch")}
                      </span>
                    )}
                  <div className={styles.buttonFlexEnd}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      disabled={
                        disablePasswordSubmit
                          ? true
                          : passwordForm.newPasswordConfirmation.length > 0
                            && isMatchingPassword && isStrongPassword && !isSamePassword
                            ? false
                            : true
                      }
                      onClick={handlePasswordSubmit}
                    >
                      {t("changePassword.changePassword")}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </Section>
        </PageContent>
      </div>
    </DashboardLayout>
  );
}
